import { useInfrastructureWizard } from '@/stores/infrastructureWizard'
import type { VmSizeCheck } from '@/types/infrastructure'
import { choiceOf, sizeWhere } from '@/utils/vmSizes'
import styles from './Infrastructure.module.css'

interface VmSizeNoticeProps {
  check: VmSizeCheck | undefined
  /** Why Azure could not be asked, when it could not (from the lookup). Shown quietly: the size is built as shown. */
  note?: string
  regionLabel?: string
}

/**
 * What the user is told about a new server's VM size in the chosen region. A size that is available says nothing. A size the
 * subscription cannot use there has the nearest series of the same size offered, and this says which and why, and whether the user has
 * agreed to it yet (the choice itself is made in the notice at the top of the Servers step: VmSizeBanner). A size nothing can replace
 * blocks Next until another region is chosen.
 */
export function VmSizeNotice({ check, note, regionLabel }: VmSizeNoticeProps) {
  const choice = useInfrastructureWizard((s) => (check ? choiceOf(check, s.vmSizeChoices, sizeWhere(s)) : null))
  if (!check) return null
  if (check.status === 'substituted') {
    return (
      <div className={styles.notice} role="status" data-testid={`vm-size-notice-${check.tier}`}>
        <span>
          {check.message}{' '}
          {choice === 'use'
            ? `You chose to build ${check.effective} instead.`
            : `Choose how to continue in the notice at the top of this step: nothing is built until you do.`}
        </span>
      </div>
    )
  }
  if (check.status === 'unavailable') {
    return (
      <div className={styles.alert} role="alert" data-testid={`vm-size-notice-${check.tier}`}>
        <span>{check.message}</span>
      </div>
    )
  }
  if (check.status === 'unchecked' && note) {
    return (
      <p className={styles.note} data-testid={`vm-size-notice-${check.tier}`}>
        Could not check whether {check.requested} is available{regionLabel ? ` in ${regionLabel}` : ''}: {note} It is built as shown.
      </p>
    )
  }
  return null
}
